import React, { useContext, useId, useState } from 'react';
import { PageFeedbackContext, PageNotice } from '../PageFeedback';
import { AssetIcon } from '../../assets';
import {
  columnsFromFormSchema,
  formatFieldValue,
  getFieldValue,
  numericValue,
  sortTableRows,
} from '../../data/formSchema';
import RowActions from './RowActions';
import IconActions from '../IconActions';
import './data-table.css';

const EMPTY = [];
const INTERACTIVE =
  'button, a, input, select, textarea, summary, [role="button"], [role="checkbox"], [contenteditable="true"]';

export function DataTableTabs({ views, activeViewId, onViewChange, label, id }) {
  return (
    <div className="wt-data-table-tabs" role="tablist" aria-label={label}>
      {views.map((view, index) => (
        <button
          key={view.id}
          type="button"
          role="tab"
          id={`${id}-tab-${view.id}`}
          aria-controls={`${id}-panel`}
          aria-selected={activeViewId === view.id}
          tabIndex={activeViewId === view.id ? 0 : -1}
          className={`wt-data-table-tab${activeViewId === view.id ? ' is-active' : ''}`}
          onClick={() => onViewChange(view.id)}
          onKeyDown={(event) => {
            const next =
              event.key === 'Home'
                ? 0
                : event.key === 'End'
                  ? views.length - 1
                  : event.key === 'ArrowRight'
                    ? (index + 1) % views.length
                    : event.key === 'ArrowLeft'
                      ? (index - 1 + views.length) % views.length
                      : null;
            if (next === null) return;
            event.preventDefault();
            event.currentTarget.parentElement.children[next]?.focus();
            onViewChange(views[next].id);
          }}
        >
          {view.iconKey ? (
            <span className={`wt-data-table-tab__icon wt-tone wt-tone--${view.tone || 'info'} wt-tone--icon`}>
              <AssetIcon name={view.iconKey} size={16} />
            </span>
          ) : null}
          <span>{view.label}</span>
          {view.count != null ? <span className="wt-data-table-tab__count">{view.count}</span> : null}
        </button>
      ))}
    </div>
  );
}

export function TableFieldValue({ row, column }) {
  const value = getFieldValue(row, column.accessor || column.id);
  const formatted = formatFieldValue(value, column);
  if (column.type === 'currency') {
    const number = numericValue(value);
    const tone =
      number == null || number === 0
        ? ''
        : column.direction === 'expense' || (!column.direction && number < 0)
          ? 'danger'
          : 'success';
    return (
      <strong className={`wt-data-table__amount${tone ? ` wt-tone wt-tone--${tone} wt-tone--icon` : ''}`}>
        {formatted}
      </strong>
    );
  }
  if (column.badge && value != null && value !== '')
    return <span className="wt-data-table__badge">{formatted}</span>;
  return column.primary ? <strong className="wt-data-table__primary">{formatted}</strong> : formatted;
}

export default function DataTable({
  columns = EMPTY,
  formSchema,
  customFields = EMPTY,
  rows = EMPTY,
  getRowId = (row) => row.id,
  loading = false,
  error = '',
  emptyState = 'Nessun risultato trovato.',
  rowActions,
  onRowClick,
  rowLabel,
  rowClassName,
  getRowProps,
  renderRowExtra,
  selectedRowId,
  title,
  description,
  ariaLabel = title || 'Elenco',
  views = EMPTY,
  activeViewId,
  onViewChange,
  toolbar,
  configurable = Boolean(formSchema),
  className = '',
}) {
  const id = `table-${useId().replace(/:/g, '')}`;
  const pageFeedback = useContext(PageFeedbackContext);
  const [internalView, setInternalView] = useState(null);
  const [sortState, setSort] = useState(null);
  const [columnState, setColumnState] = useState({});
  const schemaKey = formSchema?.id || ariaLabel;
  const sort = sortState?.schemaKey === schemaKey ? sortState : null;
  const visibility = columnState[schemaKey] || {};
  const allColumns = formSchema
    ? columnsFromFormSchema(formSchema, { customFields, overrides: columns })
    : columns;
  const visibleColumns = allColumns.filter((column) => visibility[column.id] ?? !column.hidden);
  const visibleColumnCount = Math.max(1, visibleColumns.length + (rowActions ? 1 : 0));
  const currentView = views.find((view) => view.id === (activeViewId ?? internalView)) || views[0];
  const filteredRows = currentView?.filter ? rows.filter(currentView.filter) : rows;
  const sortedRows = sortTableRows(filteredRows, visibleColumns, sort);
  const countedViews = views.map((view) => ({
    ...view,
    count: view.count ?? (view.filter ? rows.filter(view.filter).length : rows.length),
  }));
  const changeView = (next) => {
    setInternalView(next);
    onViewChange?.(next);
  };
  const alignment = (column) =>
    column.align || (['currency', 'number'].includes(column.type) ? 'end' : 'start');
  const handleRowClick = (event, row) => {
    if (event.defaultPrevented || event.target.closest(INTERACTIVE)) return;
    onRowClick?.(row);
  };
  return (
    <section className={`wt-data-table-section ${className}`} aria-label={ariaLabel}>
      {title || description ? (
        <header className="wt-data-table-heading">
          <div>
            {title ? <h2>{title}</h2> : null}
            {description ? <p>{description}</p> : null}
          </div>
        </header>
      ) : null}
      <div className="wt-data-table-shell">
        {views.length > 0 || toolbar || configurable ? (
          <div className="wt-data-table-shell__header">
            {views.length ? (
              <DataTableTabs
                views={countedViews}
                activeViewId={currentView?.id}
                onViewChange={changeView}
                label={ariaLabel}
                id={id}
              />
            ) : (
              <span />
            )}
            <div className="wt-data-table-toolbar">
              {toolbar}
              {configurable ? (
                <details
                  className="wt-data-table-columns"
                  onKeyDown={(event) => {
                    if (event.key === 'Escape') {
                      event.currentTarget.open = false;
                      event.currentTarget.querySelector('summary').focus();
                    }
                  }}
                >
                  <summary aria-label={`Colonne: ${ariaLabel}`} title="Personalizza colonne">
                    <AssetIcon name="config" size={32} />
                  </summary>
                  <fieldset>
                    <legend>Colonne visibili</legend>
                    {allColumns
                      .filter((column) => column.id !== 'actions')
                      .map((column) => {
                        const checked = visibility[column.id] ?? !column.hidden;
                        return (
                          <label key={column.id}>
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={
                                checked && visibleColumns.filter((item) => item.id !== 'actions').length === 1
                              }
                              onChange={(event) =>
                                setColumnState((previous) => ({
                                  ...previous,
                                  [schemaKey]: { ...previous[schemaKey], [column.id]: event.target.checked },
                                }))
                              }
                            />
                            {column.header}
                          </label>
                        );
                      })}
                    <button
                      type="button"
                      onClick={() => {
                        setColumnState((previous) => ({ ...previous, [schemaKey]: {} }));
                        setSort(null);
                      }}
                    >
                      Ripristina colonne
                    </button>
                  </fieldset>
                </details>
              ) : null}
            </div>
          </div>
        ) : null}
        <div
          id={`${id}-panel`}
          role={views.length ? 'tabpanel' : undefined}
          aria-labelledby={views.length ? `${id}-tab-${currentView?.id}` : undefined}
        >
          <div
            className="wt-data-table-wrap"
            tabIndex={0}
            role="region"
            aria-label={`${ariaLabel}: tabella scorrevole`}
          >
            <table className="wt-data-table" aria-busy={loading || undefined}>
              <caption className="sr-only">{ariaLabel}</caption>
              <thead>
                <tr>
                  {visibleColumns.map((column) => (
                    <th
                      key={column.id}
                      scope="col"
                      className={`wt-data-table-cell--${alignment(column)} ${column.headerClassName || ''}`}
                      style={{ width: column.width, minWidth: column.minWidth }}
                      aria-sort={
                        column.sortable
                          ? sort?.id === column.id
                            ? sort.direction === 'asc'
                              ? 'ascending'
                              : 'descending'
                            : 'none'
                          : undefined
                      }
                    >
                      {column.sortable ? (
                        <button
                          type="button"
                          className="wt-data-table-sort"
                          onClick={() =>
                            setSort(
                              sort?.id === column.id && sort.direction === 'desc'
                                ? null
                                : {
                                    schemaKey,
                                    id: column.id,
                                    direction: sort?.id === column.id ? 'desc' : 'asc',
                                  }
                            )
                          }
                        >
                          {column.header}
                          <span aria-hidden="true">
                            {sort?.id === column.id ? (sort.direction === 'asc' ? '↑' : '↓') : '↕'}
                          </span>
                        </button>
                      ) : (
                        column.header ||
                        (column.id === 'actions' ? <span className="sr-only">Azioni</span> : null)
                      )}
                    </th>
                  ))}
                  {rowActions ? (
                    <th scope="col" className="wt-data-table__actions-heading">
                      <span className="sr-only">Azioni</span>
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {loading || !sortedRows.length ? (
                  <tr>
                    <td colSpan={visibleColumnCount} className="wt-data-table__state">
                      {error && !loading ? null : <div role="status">{loading ? 'Caricamento…' : emptyState}</div>}
                    </td>
                  </tr>
                ) : (
                  sortedRows.map((row) => {
                    const extra = renderRowExtra?.(row);
                    return (
                      <React.Fragment key={getRowId(row)}>
                        <tr
                          className={[
                            onRowClick ? 'wt-data-table__row--clickable' : '',
                            rowClassName?.(row) || '',
                            selectedRowId != null && String(selectedRowId) === String(getRowId(row))
                              ? 'is-selected'
                              : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          tabIndex={onRowClick ? 0 : undefined}
                          aria-label={rowLabel?.(row)}
                          aria-haspopup={onRowClick ? 'dialog' : undefined}
                          onClick={onRowClick ? (event) => handleRowClick(event, row) : undefined}
                          onKeyDown={
                            onRowClick
                              ? (event) => {
                                  if (
                                    event.target === event.currentTarget &&
                                    (event.key === 'Enter' || event.key === ' ')
                                  ) {
                                    event.preventDefault();
                                    onRowClick(row);
                                  }
                                }
                              : undefined
                          }
                          {...getRowProps?.(row)}
                        >
                          {visibleColumns.map((column) => (
                            <td
                              key={column.id}
                              className={`wt-data-table-cell--${alignment(column)} ${column.id === 'actions' ? 'wt-data-table__actions' : ''} ${column.cellClassName || ''}`}
                              style={{ minWidth: column.minWidth }}
                            >
                              {column.render ? (
                                column.id === 'actions' ? <IconActions>{column.render(row)}</IconActions> : column.render(row)
                              ) : (
                                <TableFieldValue row={row} column={column} />
                              )}
                            </td>
                          ))}
                          {rowActions ? (
                            <td
                              className="wt-data-table__actions"
                              onClick={(event) => event.stopPropagation()}
                            >
                              <RowActions actions={rowActions(row)} />
                            </td>
                          ) : null}
                        </tr>
                        {extra ? (
                          <tr className="wt-data-table__extra">
                            <td colSpan={visibleColumnCount}>{extra}</td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
        {error && !loading && error !== pageFeedback?.filterError ? (
          <PageNotice>
            {error}
          </PageNotice>
        ) : null}
      </div>
    </section>
  );
}
